/**
 * OptionsAISuggestions — reviews the live options chain for the ticker and
 * suggests calls/puts with strike, expiration, confidence, and explicit
 * sell guidelines (stop-loss % / $ and take-profit % / $).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, Zap, TrendingUp, TrendingDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API } from "../config/api";

export default function OptionsAISuggestions({ ticker }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { setData(null); setErr(null); }, [ticker]);

  const generate = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await axios.get(`${API}/chart-ai/options-suggestions/${ticker}`, { timeout: 90000 });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Options analysis failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-slate-900/40 border-slate-700 mt-3" data-testid="options-ai-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-white flex items-center gap-2 flex-wrap">
          <Zap className="w-5 h-5 text-yellow-400" />
          AI Options Suggestions
          <span className="text-xs text-slate-400 font-normal">
            reviews the live options chain + page data · {ticker}
          </span>
          <button
            type="button"
            onClick={generate}
            disabled={busy}
            data-testid="options-ai-generate"
            className="ml-auto inline-flex items-center gap-1.5 rounded border border-yellow-700 bg-yellow-800/50 px-3 py-1.5 text-xs font-semibold text-yellow-50 hover:bg-yellow-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Analyzing chain…" : data ? "Re-analyze" : "Analyze Options Chain"}
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {err && <div className="text-sm text-rose-300 bg-rose-950/40 border border-rose-700 rounded p-3" data-testid="options-ai-error">{err}</div>}
        {!data && !busy && !err && (
          <p className="text-sm text-slate-400">
            Click <span className="text-yellow-300 font-semibold">Analyze Options Chain</span> — AI reviews strikes within ±8% of spot
            across the nearest, ~30-day, ~60-day and LEAP-style 3-6 month expirations, then suggests contracts with confidence, stop-loss and profit-taking levels.
          </p>
        )}
        {busy && (
          <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="options-ai-thinking">
            <RefreshCw className="w-4 h-4 animate-spin" /> reviewing the options chain, IV, volume and open interest…
          </div>
        )}
        {data && (
          <>
            {data.market_view && (
              <div className="text-sm text-slate-200 bg-slate-950/60 border border-slate-700 rounded p-3" data-testid="options-ai-view">
                <span className="text-[10px] uppercase tracking-wider text-yellow-400 font-bold block mb-1">Market View · spot ${data.spot}</span>
                {data.market_view}
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {(data.suggestions || []).map((s, i) => {
                const isCall = String(s.type).toUpperCase() === "CALL";
                return (
                  <div key={i} className="rounded-lg border border-slate-700 bg-slate-950/60 p-3 space-y-2" data-testid={`options-ai-suggestion-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-bold ${isCall ? "bg-green-900/50 text-green-300 border border-green-700" : "bg-red-900/50 text-red-300 border border-red-700"}`}>
                        {isCall ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {String(s.type).toUpperCase()}
                      </span>
                      <span className="text-white font-bold">${s.strike}</span>
                      <span className="text-slate-400 text-xs">exp {s.expiration}</span>
                      <span className="ml-auto text-xs font-mono text-cyan-300" title="AI confidence">
                        {s.confidence_pct}% conf
                      </span>
                    </div>
                    {s.premium_est != null && (
                      <div className="text-xs text-slate-300">Est. premium: <span className="text-white font-semibold">${s.premium_est}</span> / contract</div>
                    )}
                    <p className="text-xs text-slate-300 leading-relaxed">{s.rationale}</p>
                    <div className="text-xs rounded bg-slate-900 border border-slate-700 p-2 space-y-1">
                      <div className="text-rose-300">
                        Stop-loss: <span className="font-semibold">-{s.stop_loss_pct}%</span> or <span className="font-semibold">${s.stop_loss_price}</span>
                      </div>
                      <div className="text-emerald-300">
                        Take profit: <span className="font-semibold">+{s.take_profit_pct}%</span> or <span className="font-semibold">${s.take_profit_price}</span>
                      </div>
                      {s.sell_guideline && <div className="text-slate-400 pt-1 border-t border-slate-800">{s.sell_guideline}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-500">
              Expirations reviewed: {(data.expirations_reviewed || []).join(" · ")} — AI-generated, not financial advice.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
